/**
 * MCP Billing Rules Admin Page
 *
 * Lets super_admin / ops_admin maintain per-MCP billing rules and Community
 * Supports menus without code changes. Edits take effect immediately for
 * future claim validation runs.
 *
 * Backed by /api/admin/mcp-billing/{rules,cs-menu,payers}.
 */

import { useState, useEffect, useMemo } from 'react';
import { getWeaveinApiUrl } from '@/src/config';
import CapabilityGate from '@/components/common/CapabilityGate';
import { PageHeader, PageContainer, PageState } from '@/components/weavein';
import { Trash2, Plus, Save, ExternalLink } from 'lucide-react';

interface Payer {
  id: number;
  payer_name: string;
  payer_category: string;
  edi_payer_id: string;
  timely_filing_days: number;
}

interface Rule {
  id: number;
  payer_id: number;
  payer_name: string;
  program_type: string;
  rule_key: string;
  rule_value: any;
  effective_date: string;
  expiration_date: string | null;
  source_url: string | null;
  notes: string | null;
}

interface CsMenuRow {
  id: number;
  payer_id: number;
  payer_name: string;
  cs_service_code: string;
  cs_service_label: string;
  is_offered: boolean;
  requires_prior_auth: boolean;
  authorization_form_url: string | null;
  effective_date: string;
  expiration_date: string | null;
  notes: string | null;
}

const PROGRAM_TYPES = ['ECM', 'CS', 'CHW', 'Doula', 'ALL'];
const COMMON_RULE_KEYS = [
  'required_modifier',
  'forbidden_modifier',
  'allowed_pos',
  'forbidden_pos',
  'timely_filing_days',
  'submission_channel',
  'requires_attached_note',
  'requires_referral_id',
  'requires_prior_auth',
  'npi_type',
  'attachment_type_required',
  'min_documentation_minutes',
  'prior_auth_exempt_codes',
  'add_on_codes',
];

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
}

export default function McpBillingRulesPage() {
  const organizationId = Number(localStorage.getItem('active_cbo_id')) || null;
  const [activeTab, setActiveTab] = useState<'rules' | 'cs-menu'>('rules');
  const [payers, setPayers] = useState<Payer[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [cs, setCs] = useState<CsMenuRow[]>([]);
  const [payerFilter, setPayerFilter] = useState<number | ''>('');
  const [programFilter, setProgramFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Inline-edit state for new rule row
  const [newRule, setNewRule] = useState<Partial<Rule>>({
    program_type: 'ECM',
    rule_key: 'required_modifier',
    rule_value: '[]',
  });
  const [newCs, setNewCs] = useState<Partial<CsMenuRow>>({
    is_offered: true,
    requires_prior_auth: true,
  });

  useEffect(() => {
    void Promise.all([fetchPayers(), fetchRules(), fetchCs()]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    void fetchRules();
  }, [payerFilter, programFilter]);

  useEffect(() => {
    void fetchCs();
  }, [payerFilter]);

  async function fetchPayers() {
    const r = await fetch(getWeaveinApiUrl('/api/admin/mcp-billing/payers'), {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (r.ok) setPayers(await r.json());
  }

  async function fetchRules() {
    const params = new URLSearchParams();
    if (payerFilter) params.set('payer_id', String(payerFilter));
    if (programFilter) params.set('program_type', programFilter);
    const r = await fetch(getWeaveinApiUrl(`/api/admin/mcp-billing/rules?${params}`), {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (r.ok) setRules(await r.json());
  }

  async function fetchCs() {
    const params = new URLSearchParams();
    if (payerFilter) params.set('payer_id', String(payerFilter));
    const r = await fetch(getWeaveinApiUrl(`/api/admin/mcp-billing/cs-menu?${params}`), {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (r.ok) setCs(await r.json());
  }

  async function saveRule(rule: Partial<Rule>) {
    setSaving(true);
    try {
      const body = {
        ...rule,
        rule_value:
          typeof rule.rule_value === 'string' ? JSON.parse(rule.rule_value || 'null') : rule.rule_value,
      };
      const r = await fetch(getWeaveinApiUrl('/api/admin/mcp-billing/rules'), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        alert(`Save failed: ${err.detail || r.statusText}`);
      } else {
        await fetchRules();
      }
    } catch (e: any) {
      alert(`Save failed: ${e?.message || e}. Check JSON for rule_value.`);
    } finally {
      setSaving(false);
    }
  }

  async function deleteRule(id: number) {
    if (!confirm('Delete this rule? Future validation runs will no longer apply it.')) return;
    await fetch(getWeaveinApiUrl(`/api/admin/mcp-billing/rules/${id}`), {
      method: 'DELETE',
      credentials: 'include',
      headers: authHeaders(),
    });
    await fetchRules();
  }

  async function saveCs(row: Partial<CsMenuRow>) {
    setSaving(true);
    const r = await fetch(getWeaveinApiUrl('/api/admin/mcp-billing/cs-menu'), {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify(row),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      alert(`Save failed: ${err.detail || r.statusText}`);
    } else {
      await fetchCs();
    }
    setSaving(false);
  }

  async function deleteCs(id: number) {
    if (!confirm('Delete this CS-menu entry?')) return;
    await fetch(getWeaveinApiUrl(`/api/admin/mcp-billing/cs-menu/${id}`), {
      method: 'DELETE',
      credentials: 'include',
      headers: authHeaders(),
    });
    await fetchCs();
  }

  const payerName = useMemo(
    () => Object.fromEntries(payers.map((p) => [p.id, p.payer_name])),
    [payers]
  );

  const header = (
    <PageHeader
      eyebrow="Admin · Billing"
      title="MCP Billing Rules"
      description="Per-MCP rules and Community Supports menus. Edits take effect immediately for new claim validation runs. Restricted to super_admin / ops_admin."
      tabs={[
        { key: 'rules', label: 'Rules' },
        { key: 'cs-menu', label: 'CS Menu' },
      ]}
      activeTab={activeTab}
      onTabChange={(key) => setActiveTab(key as typeof activeTab)}
    />
  );

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        {header}
        <PageContainer>
          <PageState variant="loading" title="Loading…" />
        </PageContainer>
      </div>
    );
  }

  return (
    <CapabilityGate
      organizationId={organizationId}
      capability="billing"
      behavior="show-upgrade"
      upgradeMessage="Billing is not enabled for your organization."
    >
      <div className="flex h-full flex-col">
        {header}
        <PageContainer>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <select
            value={payerFilter}
            onChange={(e) => setPayerFilter(e.target.value ? Number(e.target.value) : '')}
            style={{ padding: 8 }}
          >
            <option value="">All MCPs</option>
            {payers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.payer_name} ({p.payer_category})
              </option>
            ))}
          </select>
          {activeTab === 'rules' && (
            <select
              value={programFilter}
              onChange={(e) => setProgramFilter(e.target.value)}
              style={{ padding: 8 }}
            >
              <option value="">All programs</option>
              {PROGRAM_TYPES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Rules tab */}
        {activeTab === 'rules' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>MCP</th>
                  <th style={{ padding: 8 }}>Program</th>
                  <th style={{ padding: 8 }}>Rule key</th>
                  <th style={{ padding: 8 }}>Value (JSON)</th>
                  <th style={{ padding: 8 }}>Effective</th>
                  <th style={{ padding: 8 }}>Source</th>
                  <th style={{ padding: 8 }}></th>
                </tr>
              </thead>
              <tbody>
                {/* New row */}
                <tr style={{ background: '#f9fafb' }}>
                  <td style={{ padding: 6 }}>
                    <select
                      value={newRule.payer_id || ''}
                      onChange={(e) => setNewRule((r) => ({ ...r, payer_id: Number(e.target.value) }))}
                      style={{ padding: 4, width: '100%' }}
                    >
                      <option value="">— pick —</option>
                      {payers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.payer_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 6 }}>
                    <select
                      value={newRule.program_type || 'ECM'}
                      onChange={(e) =>
                        setNewRule((r) => ({ ...r, program_type: e.target.value }))
                      }
                      style={{ padding: 4, width: '100%' }}
                    >
                      {PROGRAM_TYPES.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 6 }}>
                    <input
                      list="rule-keys"
                      value={newRule.rule_key || ''}
                      onChange={(e) => setNewRule((r) => ({ ...r, rule_key: e.target.value }))}
                      style={{ padding: 4, width: '100%' }}
                    />
                    <datalist id="rule-keys">
                      {COMMON_RULE_KEYS.map((k) => (
                        <option key={k} value={k} />
                      ))}
                    </datalist>
                  </td>
                  <td style={{ padding: 6 }}>
                    <textarea
                      value={
                        typeof newRule.rule_value === 'string'
                          ? newRule.rule_value
                          : JSON.stringify(newRule.rule_value || null)
                      }
                      onChange={(e) =>
                        setNewRule((r) => ({ ...r, rule_value: e.target.value }))
                      }
                      placeholder='e.g. ["U1"]  or  true  or  {"G0022":"G0019"}'
                      style={{ padding: 4, width: '100%', minHeight: 32, fontFamily: 'monospace' }}
                    />
                  </td>
                  <td style={{ padding: 6 }}>
                    <input
                      type="date"
                      value={newRule.effective_date || ''}
                      onChange={(e) =>
                        setNewRule((r) => ({ ...r, effective_date: e.target.value }))
                      }
                      style={{ padding: 4 }}
                    />
                  </td>
                  <td style={{ padding: 6 }}>
                    <input
                      value={newRule.source_url || ''}
                      onChange={(e) =>
                        setNewRule((r) => ({ ...r, source_url: e.target.value }))
                      }
                      placeholder="APL link"
                      style={{ padding: 4, width: '100%' }}
                    />
                  </td>
                  <td style={{ padding: 6 }}>
                    <button
                      className="btn btn-primary"
                      disabled={saving || !newRule.payer_id || !newRule.rule_key}
                      onClick={() => saveRule(newRule)}
                      title="Save rule"
                    >
                      <Plus size={14} />
                    </button>
                  </td>
                </tr>
                {/* Existing rows */}
                {rules.map((r) => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: 8 }}>{r.payer_name}</td>
                    <td style={{ padding: 8 }}>{r.program_type}</td>
                    <td style={{ padding: 8 }}>
                      <code>{r.rule_key}</code>
                    </td>
                    <td style={{ padding: 8 }}>
                      <code style={{ fontSize: 12 }}>{JSON.stringify(r.rule_value)}</code>
                    </td>
                    <td style={{ padding: 8 }}>{r.effective_date}</td>
                    <td style={{ padding: 8 }}>
                      {r.source_url ? (
                        <a href={r.source_url} target="_blank" rel="noreferrer">
                          <ExternalLink size={14} />
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ padding: 8 }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => deleteRule(r.id)}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
              {rules.length} rule{rules.length === 1 ? '' : 's'} shown.
            </p>
          </div>
        )}

        {/* CS Menu tab */}
        {activeTab === 'cs-menu' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: 8 }}>MCP</th>
                  <th style={{ padding: 8 }}>Code</th>
                  <th style={{ padding: 8 }}>Label</th>
                  <th style={{ padding: 8 }}>Offered</th>
                  <th style={{ padding: 8 }}>PA</th>
                  <th style={{ padding: 8 }}>Auth form</th>
                  <th style={{ padding: 8 }}>Effective</th>
                  <th style={{ padding: 8 }}></th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: '#f9fafb' }}>
                  <td style={{ padding: 6 }}>
                    <select
                      value={newCs.payer_id || ''}
                      onChange={(e) => setNewCs((r) => ({ ...r, payer_id: Number(e.target.value) }))}
                      style={{ padding: 4, width: '100%' }}
                    >
                      <option value="">— pick —</option>
                      {payers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.payer_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 6 }}>
                    <input
                      value={newCs.cs_service_code || ''}
                      onChange={(e) => setNewCs((r) => ({ ...r, cs_service_code: e.target.value }))}
                      placeholder="H0043"
                      style={{ padding: 4, width: '100%' }}
                    />
                  </td>
                  <td style={{ padding: 6 }}>
                    <input
                      value={newCs.cs_service_label || ''}
                      onChange={(e) => setNewCs((r) => ({ ...r, cs_service_label: e.target.value }))}
                      style={{ padding: 4, width: '100%' }}
                    />
                  </td>
                  <td style={{ padding: 6, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!newCs.is_offered}
                      onChange={(e) => setNewCs((r) => ({ ...r, is_offered: e.target.checked }))}
                    />
                  </td>
                  <td style={{ padding: 6, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!newCs.requires_prior_auth}
                      onChange={(e) =>
                        setNewCs((r) => ({ ...r, requires_prior_auth: e.target.checked }))
                      }
                    />
                  </td>
                  <td style={{ padding: 6 }}>
                    <input
                      value={newCs.authorization_form_url || ''}
                      onChange={(e) =>
                        setNewCs((r) => ({ ...r, authorization_form_url: e.target.value }))
                      }
                      placeholder="https://"
                      style={{ padding: 4, width: '100%' }}
                    />
                  </td>
                  <td style={{ padding: 6 }}>
                    <input
                      type="date"
                      value={newCs.effective_date || ''}
                      onChange={(e) => setNewCs((r) => ({ ...r, effective_date: e.target.value }))}
                      style={{ padding: 4 }}
                    />
                  </td>
                  <td style={{ padding: 6 }}>
                    <button
                      className="btn btn-primary"
                      disabled={saving || !newCs.payer_id || !newCs.cs_service_code}
                      onClick={() => saveCs(newCs)}
                    >
                      <Plus size={14} />
                    </button>
                  </td>
                </tr>
                {cs.map((row) => (
                  <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: 8 }}>{row.payer_name}</td>
                    <td style={{ padding: 8 }}>
                      <code>{row.cs_service_code}</code>
                    </td>
                    <td style={{ padding: 8 }}>{row.cs_service_label}</td>
                    <td style={{ padding: 8 }}>{row.is_offered ? '✓' : '—'}</td>
                    <td style={{ padding: 8 }}>{row.requires_prior_auth ? 'Yes' : 'No'}</td>
                    <td style={{ padding: 8 }}>
                      {row.authorization_form_url ? (
                        <a href={row.authorization_form_url} target="_blank" rel="noreferrer">
                          <ExternalLink size={14} />
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ padding: 8 }}>{row.effective_date}</td>
                    <td style={{ padding: 8 }}>
                      <button className="btn btn-secondary" onClick={() => deleteCs(row.id)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
              {cs.length} CS-menu entr{cs.length === 1 ? 'y' : 'ies'} shown.
            </p>
          </div>
        )}
        </PageContainer>
      </div>
    </CapabilityGate>
  );
}
